package com.upcominds.expedite.data.admin

import com.upcominds.expedite.data.NetworkClient
import com.upcominds.expedite.dto.*
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.model.Community
import com.upcominds.expedite.model.Quiz
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json

class AdminRepositoryImpl : AdminRepository {
    private val httpClient: HttpClient = NetworkClient.httpClient
    private val json = Json { ignoreUnknownKeys = true }

    private suspend fun handleError(response: HttpResponse) {
        if (!response.status.isSuccess()) {
            val bodyText = try { response.bodyAsText() } catch (e: Exception) { "" }
            val message = try {
                json.decodeFromString<ApiError>(bodyText).message
            } catch (e: Exception) {
                "\"message\"\\s*:\\s*\"([^\"]+)\"".toRegex()
                    .find(bodyText)?.groupValues?.get(1)
                    ?: if (bodyText.length < 100) bodyText.ifBlank { "Error ${response.status}" }
                       else "Error ${response.status}"
            }
            throw Exception(message)
        }
    }

    override suspend fun getAdminContext(): AdminContextDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/context"))
        handleError(response)
        return response.body<AdminContextDTO>()
    }

    override suspend fun getMobileLogs(page: Int, size: Int): MobileLogPageDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/mobile-logs")) {
            parameter("page", page)
            parameter("size", size)
        }
        handleError(response)
        return response.body<MobileLogPageDTO>()
    }

    override suspend fun getAllCommunities(): List<Community> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/communities"))
        handleError(response)
        return response.body<List<Community>>()
    }

    override suspend fun createCommunity(request: CommunityCreateRequest): Community {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/communities")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<Community>()
    }

    override suspend fun updateCommunity(communityId: String, request: CommunityUpdateRequest): Community {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/communities/$communityId")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<Community>()
    }

    override suspend fun deleteCommunity(id: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/communities/$id"))
        handleError(response)
    }

    override suspend fun getCommunityAdministratorCandidates(
        communityId: String
    ): List<CommunityAdministratorCandidateDTO> {
        val response = httpClient.get(
            NetworkClient.endpoint("api/admin/communities/$communityId/administrators")
        )
        handleError(response)
        return response.body<List<CommunityAdministratorCandidateDTO>>()
    }

    override suspend fun assignCommunityAdministrator(
        communityId: String,
        userId: String
    ): CommunityAdministratorCandidateDTO {
        val response = httpClient.post(
            NetworkClient.endpoint("api/admin/communities/$communityId/administrators")
        ) {
            contentType(ContentType.Application.Json)
            setBody(AssignCommunityAdministratorRequest(userId))
        }
        handleError(response)
        return response.body<CommunityAdministratorCandidateDTO>()
    }

    override suspend fun removeCommunityAdministrator(communityId: String, userId: String) {
        val response = httpClient.delete(
            NetworkClient.endpoint("api/admin/communities/$communityId/administrators/$userId")
        )
        handleError(response)
    }

    override suspend fun getCompetitions(): List<CommunityCompetitionDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/competitions"))
        handleError(response)
        return response.body<List<CommunityCompetitionDTO>>()
    }

    override suspend fun getCompetition(id: String): CommunityCompetitionDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/competitions/$id"))
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun getCompetitionLeaderboard(id: String): CommunityCompetitionLeaderboardDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/competitions/$id/leaderboard"))
        handleError(response)
        return response.body<CommunityCompetitionLeaderboardDTO>()
    }

    override suspend fun createCompetition(request: CommunityCompetitionCreateRequest): CommunityCompetitionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/competitions")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun updateCompetition(
        id: String,
        request: CommunityCompetitionCreateRequest
    ): CommunityCompetitionDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/competitions/$id")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun updateCompetitionSchedule(
        id: String,
        request: CompetitionScheduleUpdateRequest
    ): CommunityCompetitionDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/competitions/$id/schedule")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun setCompetitionCommunities(
        id: String,
        communityIds: List<String>
    ): CommunityCompetitionDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/competitions/$id/communities")) {
            contentType(ContentType.Application.Json)
            setBody(CompetitionCommunitySelectionRequest(communityIds))
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun createCompetitionChallenge(
        id: String,
        request: ChallengeCreateRequest
    ): CommunityCompetitionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/competitions/$id/challenges")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun updateCompetitionChallenge(
        id: String,
        challengeId: String,
        request: ChallengeCreateRequest
    ): CommunityCompetitionDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/competitions/$id/challenges/$challengeId")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun deleteCompetitionChallenge(id: String, challengeId: String): CommunityCompetitionDTO {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/competitions/$id/challenges/$challengeId"))
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun activateCompetition(id: String): CommunityCompetitionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/competitions/$id/activate"))
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun completeCompetition(id: String): CommunityCompetitionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/competitions/$id/complete"))
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun endCompetitionEarly(id: String): CommunityCompetitionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/competitions/$id/end-early"))
        handleError(response)
        return response.body<CommunityCompetitionDTO>()
    }

    override suspend fun deleteCompetition(id: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/competitions/$id"))
        handleError(response)
    }

    override suspend fun getCommunityChallenges(communityId: String): List<ChallengeDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/communities/${communityId}/challenges"))
        handleError(response)
        return response.body<List<ChallengeDTO>>()
    }

    override suspend fun createChallenge(request: ChallengeCreateRequest, communityId: String): ChallengeDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/communities/${communityId}/challenges")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<ChallengeDTO>()
    }

    override suspend fun updateChallenge(communityId: String, challengeId: String, request: ChallengeUpdateRequest): ChallengeDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/communities/${communityId}/challenges/${challengeId}")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<ChallengeDTO>()
    }

    override suspend fun deleteChallenge(communityId: String, challengeId: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/communities/${communityId}/challenges/${challengeId}"))
        handleError(response)
    }

    override suspend fun createQuiz(quiz: Quiz): Quiz {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/challenges/${quiz.challengeId}/quizzes")) {
            contentType(ContentType.Application.Json)
            setBody(quiz)
        }
        handleError(response)
        return response.body<Quiz>()
    }

    override suspend fun getCommunityNews(communityId: String): List<CommunityNewsDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/communities/${communityId}/news"))
        handleError(response)
        return response.body<List<CommunityNewsDTO>>()
    }

    override suspend fun createCommunityNews(communityId: String, request: NewsCreateRequest): CommunityNewsDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/communities/${communityId}/news")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityNewsDTO>()
    }

    override suspend fun updateCommunityNews(communityId: String, newsId: String, request: NewsUpdateRequest): CommunityNewsDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/communities/${communityId}/news/${newsId}")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<CommunityNewsDTO>()
    }

    override suspend fun deleteCommunityNews(communityId: String, newsId: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/communities/${communityId}/news/${newsId}"))
        handleError(response)
    }

    override suspend fun getSponsors(): List<SponsorDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/sponsors"))
        handleError(response)
        return response.body<List<SponsorDTO>>()
    }

    override suspend fun createSponsor(request: SponsorCreateRequest): SponsorDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/sponsors")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<SponsorDTO>()
    }

    override suspend fun updateSponsor(id: String, request: SponsorCreateRequest): SponsorDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/sponsors/$id")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<SponsorDTO>()
    }

    override suspend fun getRewards(): List<RewardDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/rewards"))
        handleError(response)
        return response.body<List<RewardDTO>>()
    }

    override suspend fun createReward(sponsorId: String, request: RewardCreateRequest): RewardDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/sponsors/$sponsorId/rewards")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<RewardDTO>()
    }

    override suspend fun updateReward(id: String, request: RewardCreateRequest): RewardDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/rewards/$id")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<RewardDTO>()
    }

    override suspend fun markRedemptionUsed(code: String): RewardRedemptionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/reward-redemptions/$code/redeem")) {
            contentType(ContentType.Application.Json)
        }
        handleError(response)
        return response.body<RewardRedemptionDTO>()
    }

    override suspend fun getAllQuestionnaires(): List<QuestionnaireDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/questionnaires"))
        handleError(response)
        return response.body<List<QuestionnaireDTO>>()
    }

    override suspend fun createQuestionnaire(request: QuestionnaireCreateRequest): QuestionnaireDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/questionnaires")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<QuestionnaireDTO>()
    }

    override suspend fun updateQuestionnaire(id: String, request: QuestionnaireUpdateRequest): QuestionnaireDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/questionnaires/$id")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<QuestionnaireDTO>()
    }

    override suspend fun getQuestionnaire(id: String): QuestionnaireDetailDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/questionnaires/${id}"))
        handleError(response)
        return response.body<QuestionnaireDetailDTO>()
    }

    override suspend fun addQuestionnaireQuestion(questionnaireId: String, request: QuestionnaireQuestionCreateRequest): QuestionnaireQuestionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/questionnaires/${questionnaireId}/questions")) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleError(response)
        return response.body<QuestionnaireQuestionDTO>()
    }

    override suspend fun deleteQuestionnaire(id: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/questionnaires/$id"))
        handleError(response)
    }

    override suspend fun getAllPuzzles(): List<PuzzleDTO> {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/puzzles"))
        handleError(response)
        return response.body<List<PuzzleDTO>>()
    }

    override suspend fun getPuzzle(id: String): PuzzleDTO {
        val response = httpClient.get(NetworkClient.endpoint("api/admin/puzzles/${id}"))
        handleError(response)
        return response.body<PuzzleDTO>()
    }

    override suspend fun createPuzzle(req: PuzzleCreateRequest): PuzzleDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/puzzles")) {
            contentType(ContentType.Application.Json)
            setBody(req)
        }
        handleError(response)
        return response.body<PuzzleDTO>()
    }

    override suspend fun updatePuzzle(id: String, req: PuzzleUpdateRequest): PuzzleDTO {
        val response = httpClient.put(NetworkClient.endpoint("api/admin/puzzles/$id")) {
            contentType(ContentType.Application.Json)
            setBody(req)
        }
        handleError(response)
        return response.body<PuzzleDTO>()
    }

    override suspend fun addPuzzleQuestion(id: String, req: PuzzleQuestionCreateRequest): PuzzleQuestionDTO {
        val response = httpClient.post(NetworkClient.endpoint("api/admin/puzzles/${id}/questions")) {
            contentType(ContentType.Application.Json)
            setBody(req)
        }
        handleError(response)
        return response.body<PuzzleQuestionDTO>()
    }

    override suspend fun deletePuzzle(id: String) {
        val response = httpClient.delete(NetworkClient.endpoint("api/admin/puzzles/$id"))
        handleError(response)
    }
}
